import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import { AnnotationTemplate } from '@paragon/contract-library';

interface AnnotationTemplatesKeyedById {
  [annotationTemplateId: number]: AnnotationTemplate;
}
interface AnnotationTemplateState {
  readonly annotationTemplates: AnnotationTemplatesKeyedById;
  readonly modifiedTemplates: readonly number[];
  readonly deletedTemplates: readonly number[];
  readonly selectedTemplate: number | null;
  /**
   * Annotation template groups are derived from `AnnotationTemplate.group`. If a new group has been created locally but
   * no templates have been added to it yet, we still want it to be a visible option wherever template groups exist.
   * Once a new group name has been created, it stays here for the whole session, since the user may associate a
   * template with it but then change their mind. These groups do not persist across sessions, so it is possible to make
   * a group, add nothing to it, refresh, and it will be gone. This case notwithstanding, this at least gives us a more
   * predictable user experience.
   */
  readonly locallyAddedTemplateGroups: readonly string[];
}

const initialState: AnnotationTemplateState = {
  annotationTemplates: {},
  modifiedTemplates: [],
  deletedTemplates: [],
  selectedTemplate: null,
  locallyAddedTemplateGroups: [],
};

const annotationTemplatesSlice = createSlice({
  name: 'annotationTemplates',
  initialState,
  reducers: {
    annotationTemplatesLoaded: (state, action: PayloadAction<AnnotationTemplate[]>) => {
      const loadedTemplates = action.payload;
      const annotationTemplates: AnnotationTemplatesKeyedById = {};

      for (const template of loadedTemplates) {
        annotationTemplates[template.id] = template;
      }

      state.annotationTemplates = castDraft(annotationTemplates);
    },
    annotationTemplateAdded: (state, action: PayloadAction<AnnotationTemplate>) => {
      const newTemplate = action.payload;
      state.annotationTemplates[newTemplate.id] = castDraft(newTemplate);
    },
    selectAnnotationTemplate: (state, action: PayloadAction<number>) => {
      const templateId = action.payload;

      if (state.annotationTemplates[templateId] != null) {
        state.selectedTemplate = templateId;
      } else {
        // TODO: how to handle errors like this?
        console.log(`Warning: no templates with id ${templateId} found`);
      }
    },
    editAnnotationTemplate: (
      state,
      action: PayloadAction<{ templateId: number; editedProperties: Partial<AnnotationTemplate> }>,
    ) => {
      const { templateId, editedProperties } = action.payload;

      if (state.annotationTemplates[templateId] != null) {
        state.annotationTemplates[templateId] = castDraft({
          ...state.annotationTemplates[templateId],
          ...editedProperties,
        });

        if (!state.modifiedTemplates.includes(templateId)) {
          state.modifiedTemplates.push(templateId);
        }
      } else {
        console.log(`Warning: couldn't find template with id ${templateId} to edit`);
      }
    },
    assignTemplateToGroup: (state, action: PayloadAction<{ templateId: number; groupName: string | null }>) => {
      const { templateId, groupName } = action.payload;

      state.annotationTemplates[templateId].group = groupName;

      if (!state.modifiedTemplates.includes(templateId)) {
        state.modifiedTemplates.push(templateId);
      }
    },
    deleteAnnotationTemplate: (state, action: PayloadAction<number>) => {
      state.deletedTemplates.push(action.payload);
      state.selectedTemplate = null;
    },
    deleteAnnotationTemplateFailed: (state, action: PayloadAction<number>) => {
      state.deletedTemplates = state.deletedTemplates.filter((template) => template !== action.payload);
      state.selectedTemplate = null;
    },
    deleteAnnotationTemplateSucceeded: (state, action: PayloadAction<number>) => {
      state.deletedTemplates = state.deletedTemplates.filter((template) => template !== action.payload);
      delete state.annotationTemplates[action.payload];
      state.selectedTemplate = null;
    },
    createTemplateGroup: (state, action: PayloadAction<string>) => {
      state.locallyAddedTemplateGroups.push(action.payload);
    },
    renameTemplateGroup: (state, action: PayloadAction<{ oldName: string; newName: string }>) => {
      const { oldName, newName } = action.payload;

      for (const template of Object.values(state.annotationTemplates)) {
        if (template.group === oldName) {
          template.group = newName;
          if (!state.modifiedTemplates.includes(template.id)) {
            state.modifiedTemplates.push(template.id);
          }
        }
      }
    },
    deleteTemplateGroup: (state, action: PayloadAction<string>) => {
      const groupName = action.payload;

      for (const template of Object.values(state.annotationTemplates)) {
        if (template.group === groupName) {
          template.group = null;
          if (!state.modifiedTemplates.includes(template.id)) {
            state.modifiedTemplates.push(template.id);
          }
        }
      }

      state.locallyAddedTemplateGroups = state.locallyAddedTemplateGroups.filter(
        (locallyAddedTemplateGroupName) => locallyAddedTemplateGroupName !== groupName,
      );
    },
    markTemplatesAsSaved: (state) => {
      state.modifiedTemplates = [];
      state.deletedTemplates = [];
    },
  },
});

export const {
  annotationTemplatesLoaded,
  annotationTemplateAdded,
  selectAnnotationTemplate,
  editAnnotationTemplate,
  assignTemplateToGroup,
  deleteAnnotationTemplate,
  deleteAnnotationTemplateFailed,
  deleteAnnotationTemplateSucceeded,
  createTemplateGroup,
  renameTemplateGroup,
  deleteTemplateGroup,
  markTemplatesAsSaved,
} = annotationTemplatesSlice.actions;
export const annotationTemplates = annotationTemplatesSlice.reducer;
